// One transaction row, composed entirely from WS-1 primitives (execution
// plan §7.3): MerchantName, CategoryChip, TypeBadge (pending/split/amended/
// removed/transfer), AmountText. Same component renders in the phone card
// feed, the tablet two-pane list and the desktop dense grid -- the
// composition is identical; only the surrounding grid/card chrome differs
// per band (execution plan §10: semantic order and available actions must
// not change across bands).
//
// Founder revision §14 (2026-09-08 founder feedback after testing the
// candidate) supersedes the earlier "scope tag always flips instantly" /
// "bottom-right apply-to-merchant tray" design:
//
//   1. Money colour is canonical (expense red / income green / transfer
//      neutral) -- see AmountText/TypeBadge/docs/canonical/product/
//      money-color-semantics.md. There is no separate per-row wiring here.
//   2. The row is a CSS grid (`merchant | category | amount`, fixed-width
//      category column) so scope pill and amount align vertically down the
//      whole list -- not flex-wrap.
//   3. Tapping the scope pill opens a small anchored menu (CategoryChip's
//      `onSelectScope`): radio Personal/Business + a checkbox "Apply to all
//      items from {merchant}". Choosing the other scope applies immediately
//      (same counterpart/catch-all PATCH as before); the checkbox, when
//      checked, chains the apply-to-similar(all_time) POST onto a
//      successful write -- D-2: that POST applies synchronously, no job is
//      registered. The SAME checkbox is offered in the TypeChooser
//      footer for a type change. The bottom-right tray/"Recent changes"/
//      jump link are REMOVED -- Undo is the small inline affordance below.
//   4. One Pending indicator (TypeBadge kind="pending", under the merchant)
//      -- AmountText no longer renders a second one.
//   5. Only the merchant name is a Link; scope/type/amount are siblings,
//      never nested inside an anchor, and carry no row-level click handler.
//
// Merged with `frontend_cont`/`e2e_wire` (main): the row's detail link
// resolves the CANONICAL transaction id (`canonical_transaction_id` when
// present, falling back to the physical `transaction_id`) via `scopeHref`
// so the return-context/privacy-scope query params survive navigation into
// the detail page (main commit 64a996a).
import { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@moneyskyline/ui";
import { AmountText } from "@/components/AmountText";
import { MerchantName } from "@/components/MerchantName";
import { CategoryChip } from "@/components/CategoryChip";
import { TypeBadge, type TypeBadgeTone } from "@/components/TypeBadge";
import { formatMoney, useMoneyFormat } from "@/lib/money";
import type { CanonicalTransactionRow, TransactionRow } from "@/api/hooks/useTransactions";
import { useCategoriesMap } from "@/api/hooks/useCategories";
import { counterpartScope, findScopeCounterpart, flipTarget, scopeOfCategory, scopeOfSlug } from "./scopeFlip";
import { TypeChooser, typeChooserContentId } from "./TypeChooser";
import { useCategoryCorrection } from "./useCategoryCorrection";
import { useShellContext } from "@/app/shell/AppShell";
import { viewportBandFromShellBand, type TelemetryContext } from "./telemetry";
import { scopeHref } from "@/app/shell/scope";

/** §14 decision 4: Pending badge tone follows the row's money colour -- omitted (neutral) for a transfer, whose sign carries no expense/income meaning. */
function pendingTone(txn: TransactionRow): TypeBadgeTone | undefined {
  if (txn.has_live_link) return undefined;
  const numeric = Number(txn.amount);
  if (numeric < 0) return "expense";
  if (numeric > 0) return "income";
  return undefined;
}

type FeedRowTransaction = TransactionRow | CanonicalTransactionRow;

export function FeedRow({ txn }: { txn: FeedRowTransaction }) {
  const location = useLocation();
  const { masked } = useMoneyFormat();
  const isCanonicalPrimary = "canonical_transaction_id" in txn;
  const detailTransactionId = isCanonicalPrimary ? txn.canonical_transaction_id : txn.transaction_id;
  const [typeChooserOpen, setTypeChooserOpen] = useState(false);
  // §4.3: the chooser anchors to the REAL type button, not the enclosing
  // CategoryChip group -- `typeButtonRef` is CategoryChip's forwarded ref
  // to that button, given to TypeChooser as a Radix `virtualRef`.
  const typeButtonRef = useRef<HTMLButtonElement>(null);
  const categoriesQuery = useCategoriesMap();
  const correction = useCategoryCorrection();
  // ENG-R1-001: SEPARATE checkbox state per surface (scope menu vs
  // TypeChooser footer), each defaulting off. §14 says the checkbox
  // defaults off "per interaction" -- a single shared state let a box
  // ticked in one surface and then dismissed (Escape, outside click) leak
  // into the OTHER surface's next write. Each is consumed and reset by its
  // own write, and also reset the instant its own surface closes without a
  // write (`onScopeMenuOpenChange`/TypeChooser's `onOpenChange`, both of
  // which Radix calls for Escape and outside click, not just an explicit
  // toggle).
  const [scopeApplyToMerchant, setScopeApplyToMerchant] = useState(false);
  const [chooserApplyToMerchant, setChooserApplyToMerchant] = useState(false);
  const { band } = useShellContext();
  const telemetryContext: TelemetryContext = {
    surface: "feed",
    viewport_band: viewportBandFromShellBand(band),
    input_method: "unknown",
  };
  const removed = txn.superseded_at !== null;
  const primaryLineItem = txn.line_items[0];
  const isSplit = txn.line_items.length > 1;
  const isAmended = txn.revision > 1 && txn.amended_from !== null;
  const merchantDisplay = txn.merchant?.display_name ?? txn.merchant_raw;
  const substituted =
    txn.merchant !== null && txn.merchant.display_name !== txn.merchant_raw;
  const scopedParams = new URLSearchParams();
  if (location.pathname === "/transactions") {
    for (const key of ["category", "merchant"]) {
      const value = new URLSearchParams(location.search).get(key);
      if (value) scopedParams.set(key, value);
    }
  }
  const detailSearch = scopedParams.toString()
    ? `?${scopedParams.toString()}`
    : "";
  // ENG-R1-002: every single-line-item, non-removed, non-split transaction
  // must be able to open the chooser -- including an uncategorized item
  // (no `category_slug` at all). Only splits/removed items stay read-only.
  const canFlip = !isSplit && !removed && !!primaryLineItem;
  const currentCategory = primaryLineItem?.category_id
    ? categoriesQuery.byId.get(primaryLineItem.category_id)
    : undefined;
  // ENG-R1-001: scope is derived from the category ROW first (a custom
  // leaf's slug carries no scope prefix), falling back to the slug prefix
  // only when the row itself can't be found (unknown/stale category_id).
  const resolvedScope =
    scopeOfCategory(currentCategory) ??
    scopeOfSlug(primaryLineItem?.category_slug) ??
    (primaryLineItem?.is_business ? "business" : null);
  const resolvedKind: "expense" | "income" | null =
    currentCategory?.kind === "expense" || currentCategory?.kind === "income"
      ? currentCategory.kind
      : null;
  const isScopedChooser =
    (resolvedScope === "personal" || resolvedScope === "business") && resolvedKind !== null;
  const typeLabel =
    !primaryLineItem?.category_id
      ? "Uncategorized"
      : categoriesQuery.data === undefined
        ? undefined
        : (currentCategory?.name ??
          "Unknown category");

  function applyPatch(
    categoryId: string,
    applyToMerchantValue: boolean,
    telemetry?: import("./useCategoryCorrection").ApplyCategoryTelemetry,
  ) {
    if (!primaryLineItem) return;
    const targetCategory = categoriesQuery.byId.get(categoryId);
    correction.applyCategory({
      lineItemId: primaryLineItem.line_item_id,
      version: primaryLineItem.version,
      categoryId,
      isBusiness: targetCategory?.is_business,
      categoryLabel: targetCategory?.name ?? "this category",
      merchant: merchantDisplay,
      previousCategoryId: primaryLineItem.category_id,
      previousIsBusiness: primaryLineItem.is_business,
      transactionId: txn.transaction_id,
      applyToMerchant: applyToMerchantValue,
      telemetry,
    });
  }

  function handleSelectScope() {
    if (!primaryLineItem?.category_slug || !categoriesQuery.data || resolvedScope === "transfer" || resolvedScope === null) return;
    const target = flipTarget(primaryLineItem.category_slug, categoriesQuery.data.categories);
    if (!target) return;
    const counterpart = findScopeCounterpart(primaryLineItem.category_slug, categoriesQuery.data.categories);
    // ENG-R1-001: consume and reset the SCOPE MENU's own checkbox state --
    // never the chooser's.
    const consumeApplyToMerchant = scopeApplyToMerchant;
    setScopeApplyToMerchant(false);
    applyPatch(target.category_id, consumeApplyToMerchant, {
      kind: "scope_flip",
      context: telemetryContext,
      fromScope: resolvedScope,
      toScope: counterpartScope(resolvedScope),
      fromTypeId: primaryLineItem.category_id,
      toTypeId: target.category_id,
      mapping: counterpart ? "counterpart" : "catch_all",
    });
  }

  function handleCreateType(parentId: string, name: string) {
    if (!primaryLineItem || !categoriesQuery.data) return;
    const parentCategory = categoriesQuery.byId.get(parentId);
    const newScope = scopeOfCategory(parentCategory);
    if (newScope !== "personal" && newScope !== "business") return;
    // ENG-R1-001: this is the TypeChooser footer's own checkbox.
    const consumeApplyToMerchant = chooserApplyToMerchant;
    setChooserApplyToMerchant(false);
    correction.createAndApply({
      parentId,
      name,
      lineItemId: primaryLineItem.line_item_id,
      version: primaryLineItem.version,
      merchant: merchantDisplay,
      previousCategoryId: primaryLineItem.category_id,
      previousIsBusiness: primaryLineItem.is_business,
      transactionId: txn.transaction_id,
      applyToMerchant: consumeApplyToMerchant,
      telemetry: {
        context: telemetryContext,
        fromTypeId: primaryLineItem.category_id,
        fromScope: resolvedScope === "transfer" ? null : resolvedScope,
        toScope: newScope,
      },
      onCreated: () => setTypeChooserOpen(false),
    });
  }

  if (removed) {
    return (
      <div
        className="grid grid-cols-1 items-start gap-x-4 gap-y-1.5 border-b border-border/60 px-3 py-2.5 text-muted-foreground last:border-b-0 min-[720px]:min-h-[56px] min-[720px]:grid-cols-[minmax(200px,1fr)_336px_112px] min-[720px]:items-center"
        data-testid="feed-row"
        {...(isCanonicalPrimary
          ? { 'data-canonical-transaction-id': txn.canonical_transaction_id }
          : { 'data-transaction-id': txn.transaction_id })}
        data-removed="true"
      >
        <div className="min-w-0">
          <div className="break-words text-sm font-medium leading-5 line-through">
            {merchantDisplay}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
            {txn.account.last4 && <span>•{txn.account.last4}</span>}
            <TypeBadge kind="removed" detail="not counted" />
          </div>
        </div>
        <div />
        <div className="shrink-0 whitespace-nowrap text-right" data-testid="feed-row-amount">
          <AmountText value={txn.amount} currency={txn.currency} struck />
        </div>
      </div>
    );
  }

  // §14 decision 5: only the merchant name is a `<Link>`; scope/type/amount
  // are siblings with no row-level click handler and no enclosing anchor.
  // §14 decision 2: a CSS grid (merchant | category | amount) at >=720px so
  // the scope pill's and the amount's x-position are identical across every
  // row; below 720px the row stacks (merchant, then category + amount).
  // DES-R1-001: hover feedback must track the actual click target -- the
  // row container itself gets NO hover styling (it has no click handler);
  // only the merchant `<Link>` below carries `hover:underline`.
  return (
    <div
      className="grid grid-cols-1 gap-1.5 border-b border-border/60 px-4 py-3 text-foreground last:border-b-0 min-[720px]:min-h-[76px] min-[720px]:grid-cols-[minmax(200px,1fr)_336px_112px] min-[720px]:items-center min-[720px]:gap-x-4 min-[720px]:gap-y-0"
      data-testid="feed-row"
      {...(isCanonicalPrimary
        ? { "data-canonical-transaction-id": txn.canonical_transaction_id }
        : { "data-transaction-id": txn.transaction_id })}
    >
      <Link
        to={scopeHref(`/transactions/item/${detailTransactionId}${detailSearch}${location.hash}`, location.search)}
        className="inline-flex w-fit min-w-0 flex-col hover:underline"
        data-testid="feed-row-link"
      >
        <div className="min-w-0 break-words text-sm font-medium leading-5">
          <MerchantName
            display={merchantDisplay}
            raw={txn.merchant_raw}
            substituted={substituted}
            className="break-words"
          />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {txn.account.last4 && <span>•{txn.account.last4}</span>}
          {txn.pending && <TypeBadge kind="pending" tone={pendingTone(txn)} />}
          {isAmended && (
            <TypeBadge
              kind="amended"
              detail={
                masked
                  ? undefined
                  : `${formatMoney(txn.amended_from, { currency: txn.currency })} → ${formatMoney(txn.amount, { currency: txn.currency })}`
              }
            />
          )}
        </div>
      </Link>

      {/* §14 decision 2: below 720px, category + amount share ONE line
          ("scope · type · amount"), not three stacked lines -- at >=720px
          this wrapper dissolves (`contents`) so its two children resume
          their explicit grid-column placement (col2/col3).
          DES-R2-002: `flex-wrap` here (rather than a fixed nowrap) is the
          row-level half of the 200%-zoom fix -- when CategoryChip's own
          rem-based floors don't fit next to the amount cell, the whole
          category group drops to its own line instead of the amount
          overlapping it. At 390px/100% text the two floors comfortably fit
          one line, so DES-F-001 is unaffected. */}
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 min-[720px]:contents">
      <div className="flex min-w-[6.5rem] flex-1 flex-wrap items-center gap-2 min-[720px]:min-w-0 min-[720px]:flex-nowrap">
        {txn.has_live_link && <TypeBadge kind="transfer" />}
        {isSplit ? (
          <CategoryChip
            path={["Split"]}
            source={primaryLineItem?.source ?? "ai"}
            split={txn.line_items.length}
            merchantName={merchantDisplay}
            className="text-xs"
          />
        ) : canFlip && categoriesQuery.data ? (
          <>
            <CategoryChip
              ref={typeButtonRef}
              path={primaryLineItem.category_slug ? primaryLineItem.category_slug.split(".") : ["Uncategorized"]}
              source={primaryLineItem.source}
              slug={primaryLineItem.category_slug}
              // §3 "Saving correction": while a write is in flight, paint
              // the DESTINATION scope+type pair immediately, not the still
              //-stale server-confirmed one -- `correction.state.target` is
              // set synchronously when the write starts (useCategoryCorrection).
              scope={correction.state.kind === "saving" ? correction.state.target.scope : resolvedScope}
              typeLabel={correction.state.kind === "saving" ? correction.state.target.categoryLabel : typeLabel}
              parentLabel={null}
              merchantName={merchantDisplay}
              fixedLayout
              onSelectScope={handleSelectScope}
              applyToMerchantChecked={scopeApplyToMerchant}
              onApplyToMerchantChange={setScopeApplyToMerchant}
              onScopeMenuOpenChange={(open) => {
                if (!open) setScopeApplyToMerchant(false);
              }}
              merchantApplyCount={correction.lastKnownCounts?.allTime ?? null}
              onOpenTypeChooser={() => setTypeChooserOpen(true)}
              saving={correction.state.kind === "saving"}
              busy={correction.saving}
              typeChooserOpen={typeChooserOpen}
              typeChooserId={typeChooserContentId(primaryLineItem.line_item_id)}
              className="text-xs"
            />
            <TypeChooser
              anchorRef={typeButtonRef}
              open={typeChooserOpen}
              onOpenChange={(open) => {
                setTypeChooserOpen(open);
                // ENG-R1-001: reset THIS surface's checkbox on every close --
                // Escape, outside click, or a real selection -- so a tick
                // that never resulted in a write can't leak into the chooser's
                // next open.
                if (!open) setChooserApplyToMerchant(false);
              }}
              lineItemId={primaryLineItem.line_item_id}
              scope={isScopedChooser ? (resolvedScope as "personal" | "business") : "any"}
              kind={isScopedChooser ? (resolvedKind as "expense" | "income") : undefined}
              categories={categoriesQuery.data.categories}
              currentCategoryId={primaryLineItem.category_id}
              currentParentId={currentCategory?.parent_id ?? null}
              merchantName={merchantDisplay}
              applyToMerchantChecked={chooserApplyToMerchant}
              onApplyToMerchantChange={setChooserApplyToMerchant}
              merchantApplyCount={correction.lastKnownCounts?.allTime ?? null}
              onSelect={(categoryId, selection) => {
                // ENG-R1-001: the chooser's own checkbox -- consumed/reset
                // here, not the scope menu's.
                const consumeApplyToMerchant = chooserApplyToMerchant;
                setChooserApplyToMerchant(false);
                const chosenCategory = categoriesQuery.byId.get(categoryId);
                const toScope = scopeOfCategory(chosenCategory);
                if (toScope !== "personal" && toScope !== "business") {
                  // Never guess a scope for the event -- fall back to an untelemetered write rather than fabricate.
                  applyPatch(categoryId, consumeApplyToMerchant);
                  return;
                }
                applyPatch(categoryId, consumeApplyToMerchant, {
                  kind: "type_change",
                  context: telemetryContext,
                  fromTypeId: primaryLineItem.category_id,
                  toTypeId: categoryId,
                  fromScope: resolvedScope === "transfer" ? null : resolvedScope,
                  toScope,
                  selection,
                });
              }}
              onCreateCategory={handleCreateType}
              busy={correction.saving}
              creating={correction.state.kind === "creating"}
              createError={
                correction.state.kind === "create_failed"
                  ? "Couldn't add this type. Try again."
                  : null
              }
              surface="feed"
              entry={
                !primaryLineItem.category_id
                  ? "uncategorized"
                  : resolvedScope === "transfer"
                    ? "transfer"
                    : isScopedChooser
                      ? "categorized"
                      : "unknown"
              }
            />
          </>
        ) : primaryLineItem?.category_slug ? (
          <CategoryChip
            path={primaryLineItem.category_slug.split(".")}
            source={primaryLineItem.source}
            slug={primaryLineItem.category_slug}
            scope={resolvedScope}
            typeLabel={typeLabel}
            typeLabelPending={categoriesQuery.data === undefined}
            merchantName={merchantDisplay}
            fixedLayout
            busy={correction.saving}
            className="text-xs"
          />
        ) : (
          !txn.has_live_link && (
            <span className="ms-tap-44 inline-flex min-h-11 items-center rounded-md border border-border bg-muted px-2.5 text-xs text-muted-foreground">
              Uncategorized
            </span>
          )
        )}
      </div>

      {/* TEST-R1-001: below 720px this amount box sits in a `justify-between`
          flex row (not the fixed-width grid column >=720px uses) -- without
          an explicit width here, a `shrink-0` box sized to its OWN text
          content puts a wider amount ("+$3,187.25") flush-right at a
          DIFFERENT x than a narrower one ("-$140.00"), breaking §14 decision
          2's "still column-aligned within the row" on mobile. A fixed width
          (matching the desktop grid's own 112px column) makes the box's
          left edge -- not just its text -- the same across every row; the
          text still right-aligns inside it. `min-[720px]:w-auto` hands
          sizing back to the grid column once it takes over. */}
      <div className="w-28 shrink-0 text-right min-[720px]:col-start-3 min-[720px]:w-auto" data-testid="feed-row-amount">
        <AmountText
          value={txn.amount}
          currency={txn.currency}
          variant={txn.has_live_link ? "transfer" : "amount"}
        />
      </div>
      </div>

      {canFlip && (
        <div className="min-[720px]:col-start-2">
          {correction.state.kind === "changed_since" && (
            <div
              className="mt-1.5 rounded-lg border border-degraded-border bg-degraded-surface p-2 text-xs"
              role="alert"
              data-testid="feed-row-scope-changed-since"
            >
              <p>
                This item changed elsewhere. Reload to see the latest before
                correcting it.
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-1.5"
                onClick={() => correction.reload()}
              >
                Reload
              </Button>
            </div>
          )}
          {/* ENG-R1-002: §7 copy only -- never a raw `error.message`.
              `rejected` (a definitive HTTP error) and `unconfirmed`
              (network/timeout -- an UNKNOWN outcome) get different copy and
              different recovery affordances, never collapsed into one
              generic "failed" message. */}
          {correction.state.kind === "rejected" && (
            <div
              className="mt-1.5 rounded-lg border border-degraded-border bg-degraded-surface p-2 text-xs"
              role="alert"
              data-testid="feed-row-scope-error"
            >
              <p>Couldn’t save this category.</p>
              <div className="mt-1.5 flex gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => correction.state.kind === "rejected" && correction.state.retry()}
                >
                  Try again
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => correction.dismiss()}>
                  Dismiss
                </Button>
              </div>
            </div>
          )}
          {correction.state.kind === "unconfirmed" && (
            <div
              className="mt-1.5 rounded-lg border border-degraded-border bg-degraded-surface p-2 text-xs"
              role="alert"
              data-testid="feed-row-scope-unconfirmed"
            >
              <p>We couldn’t confirm the change. Refresh to check its category.</p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-1.5"
                onClick={() => correction.reload()}
              >
                Refresh
              </Button>
            </div>
          )}
          {correction.state.kind === "created_apply_failed" && (
            <div
              className="mt-1.5 rounded-lg border border-degraded-border bg-degraded-surface p-2 text-xs"
              role="alert"
              data-testid="feed-row-scope-created-apply-failed"
            >
              <p>Type added, but this transaction wasn’t updated.</p>
              <div className="mt-1.5 flex gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    correction.state.kind === "created_apply_failed" && correction.state.retry()
                  }
                >
                  Try again
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => correction.dismiss()}>
                  Dismiss
                </Button>
              </div>
            </div>
          )}
          {/* §14 decision 3: no more bottom-right tray -- Undo and the
              merchant-apply status are a small inline row next to the
              changed control. */}
          {correction.state.kind === "saved" && (correction.state.info.undo || correction.state.info.merchantApply.kind !== "idle") && (
            <div
              className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground"
              data-testid="feed-row-saved-status"
            >
              <span>Saved</span>
              {correction.state.info.merchantApply.kind === "applying" && <span>· Applying to other items…</span>}
              {correction.state.info.merchantApply.kind === "applied" && (
                <>
                  {correction.state.info.merchantApply.appliedCount > 0 ? (
                    <span data-testid="feed-row-merchant-apply-count">
                      · applied to {correction.state.info.merchantApply.appliedCount} other{" "}
                      {merchantDisplay}{" "}
                      item{correction.state.info.merchantApply.appliedCount === 1 ? "" : "s"}
                    </span>
                  ) : (
                    <span data-testid="feed-row-merchant-apply-count">
                      · no other {merchantDisplay} items to update
                    </span>
                  )}
                  {/* D-3: honest about scope -- a rule was saved for FUTURE
                      charges too, distinct from the synchronous relabel just
                      performed above. */}
                  <span>A merchant rule was saved; future {merchantDisplay} charges will use it.</span>
                </>
              )}
              {correction.state.info.merchantApply.kind === "no_merchant" && (
                <span>· This transaction has no identified merchant, so it can’t be applied to others.</span>
              )}
              {correction.state.info.merchantApply.kind === "failed" && (
                <span role="alert">· Couldn’t save the merchant rule. Try again.</span>
              )}
              {correction.state.info.merchantApply.kind === "unconfirmed" && (
                <span role="alert">· We couldn’t confirm whether the merchant rule was saved. Refresh before trying again.</span>
              )}
              {correction.state.info.undo && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="ms-tap-44 h-auto min-h-0 px-2 py-0.5"
                  aria-label={`Undo category change for ${merchantDisplay ?? "this transaction"}`}
                  onClick={() => correction.undoLast()}
                  disabled={correction.state.info.undoStatus === "pending"}
                  data-testid="feed-row-undo"
                >
                  {correction.state.info.undoStatus === "pending" ? "Restoring previous category…" : "Undo"}
                </Button>
              )}
              {correction.state.info.undoStatus === "failed" && <span role="alert">Couldn’t restore. Try again.</span>}
              {correction.state.info.undoStatus === "unconfirmed" && (
                <span role="alert">We couldn’t confirm the previous category was restored. Refresh to check.</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FeedRow;
